/**
 * A client-tool OFFER — one wait of the worker on the browser — and the page's
 * once-only bookkeeping for it (#352).
 *
 * The worker's request carries the SESSION's workspace and a per-wait id (both
 * optional: a worker from before them sends neither):
 *  - R9: a resumed call's second wait reuses the call id, so "already ran this
 *    call" must be asked per WAIT — keyed by call id alone, the page that ran the
 *    first wait skipped the second and the turn stalled to the timeout.
 *  - R7: the result POST names the session's own workspace; the worker is the one
 *    party that always knows it.
 *  - R3: a surface-bound call with no stamp (an old worker) acts only in the
 *    hook's workspace, never on whichever surface happens to be focused.
 *
 * Also the page-wide bookkeeping: the once-only run set, the defer clock, the
 * kept-result store + retry pump + resume registry (#352 I3b), and the cross-TAB
 * claim that lets exactly one tab run each wait (#352 I1, at the end).
 */
import { currentSessionSignal, getApiBase, sessionEndedSince, sessionFetch } from '@papan/api-client'
import { SURFACE_BOUND_CLIENT_TOOLS, CLIENT_TOOL_TARGET_ARG, readClientToolTarget } from '../core/client-tool-target.js'

export interface ClientToolOffer { toolName: string; args: Record<string, unknown>; toolCallId: string; workspaceId?: string; waitId?: string }

/** The key for this page's once-only bookkeeping: one per WAIT. */
export function waitKey(o: { toolCallId: string; waitId?: string }): string {
  return o.waitId ? `${o.toolCallId}#${o.waitId}` : o.toolCallId
}

/** #352 R3: a surface-bound call with no stamp acts only in the hook's workspace. */
export function scopeTargetlessCall(toolName: string, args: Record<string, unknown>, hookWorkspaceId: string | undefined): Record<string, unknown> {
  if (!hookWorkspaceId || !SURFACE_BOUND_CLIENT_TOOLS.has(toolName) || readClientToolTarget(args)) return args
  return { ...args, [CLIENT_TOOL_TARGET_ARG]: { workspaceId: hookWorkspaceId } }
}

/** #352 R7: the session's own workspace — envelope, then stamped target, then the hook's. */
export function resultWorkspaceId(o: ClientToolOffer, hookWorkspaceId: string | undefined): string | undefined {
  return o.workspaceId || readClientToolTarget(o.args)?.workspaceId || hookWorkspaceId
}

/**
 * Client-tool waits this PAGE has already run, by `waitKey` — shared by every
 * mounted chat surface (#352). One session can be on screen in two at once
 * (its workspace's chat panel, which stays mounted when hidden, and the
 * chat-home view), and each polls `/pending`; a per-instance set let both run
 * the same call, applying an editor action twice — and the copy whose
 * surface was absent could answer first with a wrong "nothing is open".
 */
const clientToolCallsRun = new Set<string>()
/** Bounded: a long-lived tab runs many calls. A key only needs to be held
 *  while its run can still be re-offered (the worker's ~5 min wait). */
const CLIENT_TOOL_CALLS_RUN_MAX = 2000
function markClientToolCallRun(key: string): void {
  clientToolCallsRun.add(key)
  while (clientToolCallsRun.size > CLIENT_TOOL_CALLS_RUN_MAX) {
    const oldest = clientToolCallsRun.values().next().value
    if (oldest === undefined) break
    clientToolCallsRun.delete(oldest)
  }
}

/**
 * True = run it now (and it is marked run); false = this page already ran it.
 *
 * The two dispatch paths do NOT key alike: the model's own `tool-call-start`
 * carries no wait id (key `c`), the worker's `client-tool-request` and its
 * `/pending` replay carry one (key `c#w1`). So the bare id and its waits refuse
 * each other — either order — while two DIFFERENT waits of one call both run
 * (R9: a resumed call's second wait gets no model-call dispatch, only its own
 * request).
 */
export function takeClientToolRun(offer: { toolCallId: string; waitId?: string }): boolean {
  const key = waitKey(offer)
  if (clientToolCallsRun.has(key)) return false
  if (offer.waitId) {
    // The model's call already ran this call id.
    if (clientToolCallsRun.has(offer.toolCallId)) return false
  } else {
    // A wait of this call already ran (rare: a replayed tool-call-start after
    // the worker's request) — a linear scan of a bounded set.
    const prefix = `${offer.toolCallId}#`
    for (const k of clientToolCallsRun) if (k.startsWith(prefix)) return false
  }
  markClientToolCallRun(key)
  return true
}

/** Test-only: forget every run. */
export function resetClientToolRunsForTest(): void {
  clientToolCallsRun.clear()
}

/**
 * When each wait was FIRST deferred (ms epoch), by `waitKey` — the clock a
 * tool's `maxDeferMs` runs against. Bounded FIFO like the run set: a key only
 * matters while the worker can still re-offer it.
 */
const clientToolFirstDeferred = new Map<string, number>()

/**
 * True while this wait may still be deferred. `maxDeferMs` undefined = no cap.
 * The first call for a key starts its window (and is allowed); once the key
 * was first deferred more than `maxDeferMs` ago, it stops deferring.
 */
export function withinDeferWindow(key: string, maxDeferMs: number | undefined, now: number = Date.now()): boolean {
  if (maxDeferMs === undefined) return true
  let first = clientToolFirstDeferred.get(key)
  if (first === undefined) {
    first = now
    clientToolFirstDeferred.set(key, now)
    while (clientToolFirstDeferred.size > CLIENT_TOOL_CALLS_RUN_MAX) {
      const oldest = clientToolFirstDeferred.keys().next().value
      if (oldest === undefined) break
      clientToolFirstDeferred.delete(oldest)
    }
  }
  return now - first <= maxDeferMs
}

/** Test-only: forget every first-deferred time. */
export function resetClientToolDeferTimesForTest(): void {
  clientToolFirstDeferred.clear()
}

/** True when this page already ran (or is running) this wait — the same relation
 *  `takeClientToolRun` refuses on (the bare call id and its waits refuse each
 *  other), without marking anything. */
export function ranOnThisPage(offer: { toolCallId: string; waitId?: string }): boolean {
  if (clientToolCallsRun.has(waitKey(offer))) return true
  if (offer.waitId) return clientToolCallsRun.has(offer.toolCallId)
  const prefix = `${offer.toolCallId}#`
  for (const k of clientToolCallsRun) if (k.startsWith(prefix)) return true
  return false
}

// ─── Kept results (#352 I3b) ────────────────────────────────────────────────
//
// The page KEEPS each client-tool result and re-sends it until the server takes
// it; it never re-runs the handler. The server takes a repeat result safely: a
// suspended row keeps its FIRST answer (`answerSuspended`, a conditional UPDATE,
// and another wait's answer is refused), and a live result is consumed once by
// its waiting worker (the `waitId` guard drops a stale one). Re-running the
// handler, on the other hand, would apply its editor action twice.
//
// Re-sending is driven by the page, not by offers: `/pending` serves ONE request
// (a parallel sibling overwrites the slot; a suspended turn serves only its
// newest open row) and its poll runs only while the chat is processing. So a
// page-wide pump re-POSTs every `pending` entry with backoff until a 2xx (taken
// — `accepted:false` included: another answer won) or the entry ages out. No
// status is terminal: a 401 mid-deploy, a 403/404 after an identity switch or a
// 402 before the cost-gate fix can each recover, and giving up wrongly strands a
// suspended turn for days; a status that never recovers costs one request per
// 5 minutes until the TTL. An OFFER of a kept wait (pending OR taken) only
// nudges a re-POST: on the live path a 2xx means only "published" (the reply key
// lives 60 s), so a POST that landed in the suspend window, or before a worker
// crash, was never consumed — a server that still offers the wait is the proof.
//
// Privacy: the kept body is the tool's result — editor state already in page
// memory and in the transcript. Credentials never transit this channel (the
// `/agui` tool-result route redacts any session credential before the model sees
// it, and connection strings stay in the server-side sandbox). The mirror is
// `sessionStorage`: per origin, per tab, cleared when the tab closes.
//
// The one exception to "never re-run" (owner ruling 2026-10-19 — split by action):
// after a page RELOAD, a kept result of a REPLAYABLE, side-effect-free action
// (`AgentToolEntry.replayable`, e.g. a db_editor action without autoRun — any action
// with a truthy autoRun executes SQL) re-runs ONCE on the new page to restore the UI
// it set — the editor comes back showing the SQL — and ONLY when the server offers
// that wait to this page again (a `/pending` replay or a live `client-tool-request`:
// its turn still waits on it). Never from the pump or a page load alone: a result
// whose POST keeps failing stays `pending` for its TTL, and replaying it on every
// reload put its stale SQL over the user's later edits (test 9, 2026-10-20). A tool
// that pins its replay target (`pinReplayTarget`, db_editor) never replays an entry
// kept without a pin (an older build's: it would bind to whatever editor is ACTIVE
// now, possibly another database). It waits for the tool's readiness
// gate AND for this page to resolve the call's surface (`canResolve`: the bound
// editor is mounted), takes no claim (it is UI state on this tab only) and posts
// nothing: the kept result stays the answer and its re-send is unchanged. Per target
// (tool + stamped surface) only the NEWEST kept entry replays: an older one never
// lands on top of a newer action. A side-effecting action is never re-run, and an entry kept
// by THIS page load never replays (two surfaces on one page would apply it twice).

export type KeptState = 'pending' | 'taken'
export interface KeptResult {
  /** The tenant active when the result was kept — every re-POST sends ITS header. */
  tenantId: string | null
  toolCallId: string
  /** The page (CLIENT_ID) that ran the handler. */
  holder: string
  lastClaimAt: number
  claimEnded: boolean
  sessionId: string
  workspaceId: string | undefined
  waitId?: string
  /** The exact JSON string sent as the tool message `content`. */
  body: string
  attempts: number
  lastAt: number
  inFlight: boolean
  state: KeptState
  keptAt: number
  /** The dispatched action is side-effect free (`AgentToolEntry.replayable`), judged on
   *  the dispatched args at keep time — a reloaded page may re-run it once (above). */
  replayable: boolean
  /** The page load (CLIENT_ID) that kept it — a replay runs only on ANOTHER load. */
  keptByPage: string
  /** What the call acted on — the tool and the stamped surface (`toolName|workspace|file`);
   *  only the newest entry per target replays. Absent on an entry from an older build. */
  target?: string
  /** R-22: the file of the editor the call actually drove (from `pinReplayTarget`), when
   *  pinned — `replayArgs` then name it, so the replay binds to that file only. */
  replayFileId?: string
  /** What a replay runs — kept only for a replayable entry. */
  replayToolName?: string
  replayArgs?: Record<string, unknown>
}

export const KEPT_RESULT_TTL_MS = 24 * 60 * 60 * 1000
export const KEPT_RESULT_MAX = 200
/** ONE rule: an entry whose `waitId` is absent (an old worker, which cannot tell its
 *  waits apart) is REMOVED — pending or taken — once `keptAt + this` has passed, so
 *  it is never pumped past that worker's 5-minute wait into a resumed second wait. */
export const NO_WAIT_ID_TTL_MS = 6 * 60 * 1000
/** A taken entry's `ran` claim is refreshed this often (Task 11); starts at keptAt. */
export const CLAIM_REFRESH_MS = 5 * 60 * 1000
/** An offer never re-POSTs more than once per this. */
const OFFER_MIN_INTERVAL_MS = 5000
const RETRY_BASE_MS = 5000
const RETRY_MAX_MS = 300_000
const STORAGE_PREFIX = 'papan:ctr:'
const MIRROR_MAX_BYTES = 512 * 1024

/** Kept results by `waitKey`, oldest first (Map insertion order). */
const kept = new Map<string, KeptResult>()

function storage(): Storage | null {
  try { return typeof sessionStorage === 'undefined' ? null : sessionStorage } catch { return null }
}

function mirror(key: string, r: KeptResult): void {
  const s = storage()
  if (!s) return
  try {
    // Bytes ≥ length; a cheap bound first, the exact UTF-8 size only near it.
    const json = JSON.stringify(r)
    if (json.length > MIRROR_MAX_BYTES || new TextEncoder().encode(json).length > MIRROR_MAX_BYTES) {
      s.removeItem(STORAGE_PREFIX + key)
      return
    }
    s.setItem(STORAGE_PREFIX + key, json)
  } catch { /* quota / blocked storage — memory still holds it */ }
}

function unmirror(key: string): void {
  try { storage()?.removeItem(STORAGE_PREFIX + key) } catch { /* ignore */ }
}

function isKeptShape(v: unknown): v is KeptResult {
  const r = v as KeptResult | null
  return !!r && typeof r === 'object' && typeof r.toolCallId === 'string' && typeof r.sessionId === 'string'
    && typeof r.body === 'string' && typeof r.keptAt === 'number' && (r.state === 'pending' || r.state === 'taken')
}

/** After a reload: every mirrored entry comes back, none of them in flight. */
function loadFromStorage(): void {
  const s = storage()
  if (!s) return
  try {
    const loaded: Array<[string, KeptResult]> = []
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i)
      if (!k || !k.startsWith(STORAGE_PREFIX)) continue
      try {
        const v = JSON.parse(s.getItem(k) ?? 'null') as unknown
        // An entry mirrored by a build before replay has no flag: never replayed.
        if (isKeptShape(v)) loaded.push([k.slice(STORAGE_PREFIX.length), { ...v, inFlight: false, replayable: v.replayable === true, keptByPage: typeof v.keptByPage === 'string' ? v.keptByPage : v.holder }])
      } catch { /* a corrupt entry is skipped */ }
    }
    loaded.sort((a, b) => a[1].keptAt - b[1].keptAt)
    for (const [k, v] of loaded) kept.set(k, v)
  } catch { /* storage unreadable — start empty */ }
}
loadFromStorage()

function forget(key: string): void {
  kept.delete(key)
  unmirror(key)
}

/** Drops entries past their TTL (24 h; 6 min for one with no waitId). */
function prune(now: number = Date.now()): void {
  for (const [k, r] of kept) {
    const ttl = r.waitId ? KEPT_RESULT_TTL_MS : NO_WAIT_ID_TTL_MS
    if (now - r.keptAt > ttl) forget(k)
  }
}

export function keepResult(key: string, r: {
  tenantId: string | null; toolCallId: string; holder: string; sessionId: string; workspaceId: string | undefined; waitId?: string; body: string
  /** The page load keeping it (defaults to `holder`, which is this page's CLIENT_ID). */
  keptByPage?: string
  /** What the call acted on (`clientToolTargetKey`) — for every entry. */
  target?: string
  /** R-22: the drove file, recorded when the replay args were pinned to it. */
  replayFileId?: string
  /** Set only for a replayable action: what a reloaded page re-runs. */
  replay?: { toolName: string; args: Record<string, unknown> }
}): void {
  const now = Date.now()
  const entry: KeptResult = {
    tenantId: r.tenantId, toolCallId: r.toolCallId, holder: r.holder, sessionId: r.sessionId, workspaceId: r.workspaceId,
    ...(r.waitId ? { waitId: r.waitId } : {}), body: r.body,
    lastClaimAt: now, claimEnded: false, attempts: 0, lastAt: 0, inFlight: false, state: 'pending', keptAt: now,
    replayable: !!r.replay, keptByPage: r.keptByPage ?? r.holder, ...(r.target ? { target: r.target } : {}),
    ...(r.replay && r.replayFileId ? { replayFileId: r.replayFileId } : {}),
    ...(r.replay ? { replayToolName: r.replay.toolName, replayArgs: r.replay.args } : {}),
  }
  kept.delete(key) // re-insert as the newest
  kept.set(key, entry)
  while (kept.size > KEPT_RESULT_MAX) {
    const oldest = kept.keys().next().value
    if (oldest === undefined) break
    forget(oldest)
  }
  mirror(key, entry)
}

export function keptResult(key: string): KeptResult | null {
  prune()
  return kept.get(key) ?? null
}

/** The backoff after `attempts` failed attempts: 5 s doubling, capped at 5 min. */
export function nextRetryDelayMs(attempts: number): number {
  if (attempts <= 0) return 0
  return Math.min(RETRY_BASE_MS * 2 ** (attempts - 1), RETRY_MAX_MS)
}

/** What the PUMP posts: `pending`, not in flight, its backoff elapsed. */
export function pendingKeys(now: number = Date.now()): string[] {
  prune(now)
  const out: string[] = []
  for (const [k, r] of kept) {
    if (r.state === 'pending' && !r.inFlight && now - r.lastAt >= nextRetryDelayMs(r.attempts)) out.push(k)
  }
  return out
}

/** An offer may re-POST a kept result (pending or taken) when none is in flight and
 *  the last attempt is ≥ 5 s old — it ignores the long backoff, never more often. */
export function offerMayPost(key: string, now: number = Date.now()): boolean {
  prune(now)
  const r = kept.get(key)
  return !!r && !r.inFlight && now - r.lastAt >= OFFER_MIN_INTERVAL_MS
}

export function beginAttempt(key: string, now: number = Date.now()): void {
  const r = kept.get(key)
  if (!r) return
  r.attempts++
  r.lastAt = now
  r.inFlight = true
  mirror(key, r)
}

/** 2xx → `taken` (`accepted:false` included — another answer won); anything else
 *  stays `pending`. No status is terminal. */
export function endAttempt(key: string, status: number | 'network', accepted?: boolean): KeptState {
  void accepted // either way the server has an answer for the wait
  const ok = typeof status === 'number' && status >= 200 && status < 300
  const r = kept.get(key)
  if (!r) return ok ? 'taken' : 'pending'
  r.inFlight = false
  if (ok) r.state = 'taken'
  mirror(key, r)
  return r.state
}

export type OfferAction = 'run' | 'nudge' | 'skip'

/**
 * An offer of a wait: kept (pending OR taken) → `nudge` (re-POST, never re-run — the
 * offer resets the backoff: the server still wants it); not kept but this page is
 * running it → `skip`; else `run`.
 */
export function onOffer(key: string, ranOnThisPage: boolean): OfferAction {
  prune()
  const r = kept.get(key)
  if (r) {
    if (r.state === 'pending' && r.attempts > 1) { r.attempts = 1; mirror(key, r) }
    return 'nudge'
  }
  return ranOnThisPage ? 'skip' : 'run'
}

/** Taken entries whose `ran` claim is due a refresh (claim only, no POST). */
export function claimRefreshKeys(now: number = Date.now()): string[] {
  prune(now)
  const out: string[] = []
  for (const [k, r] of kept) {
    if (r.state === 'taken' && !r.claimEnded && now - r.lastClaimAt >= CLAIM_REFRESH_MS) out.push(k)
  }
  return out
}

/** `not-pending` ⇒ the wait is over: stop refreshing its claim. */
export function noteClaim(key: string, outcome: 'claimed' | 'lost' | 'not-pending' | 'error', now: number = Date.now()): void {
  const r = kept.get(key)
  if (!r) return
  r.lastClaimAt = now
  if (outcome === 'not-pending') r.claimEnded = true
  mirror(key, r)
}

let pumpTimer: ReturnType<typeof setInterval> | null = null
const refreshing = new Set<string>()

/**
 * The page-wide retry pump. START-ONLY and idempotent per page: no hook ever stops
 * it (a StrictMode remount, a surface unmounting), so a kept result is re-sent even
 * when no chat surface shows its session — or right after a reload.
 */
export function startResultPump(post: (key: string) => Promise<void>, refreshClaim: (key: string) => Promise<void>, intervalMs: number = 5000): void {
  if (pumpTimer !== null) return
  pumpTimer = setInterval(() => {
    // Re-send and refresh only — the pump never REPLAYS: a kept result restores its UI
    // only when the server offers its wait again (`replayKeptResult`, the offer path).
    for (const k of pendingKeys()) {
      try { void post(k).catch(() => {}) } catch { /* the post settles its own attempt */ }
    }
    for (const k of claimRefreshKeys()) {
      if (refreshing.has(k)) continue
      refreshing.add(k)
      let p: Promise<void>
      try { p = refreshClaim(k) } catch { p = Promise.resolve() }
      void p.catch(() => {}).finally(() => refreshing.delete(k))
    }
  }, intervalMs)
}

// ─── Replay after a reload (owner ruling 2026-10-19) ─────────────────────────

/** What a replay needs of a registered tool (hooks.ts passes `getAgentTool`'s entry). */
export interface ReplayTool {
  handler: (args: Record<string, unknown>, ctx: { toolCallId: string; sessionId: string }) => Promise<unknown>
  ready?: (args?: Record<string, unknown>) => boolean
  /** Can this page act on the call's surface now? A replay waits for it: run while the
   *  bound editor is not mounted yet, the handler only answers "not open" and the one
   *  replay is used up with nothing restored. */
  canResolve?: (args: Record<string, unknown>) => boolean
  /** The tool's CURRENT `AgentToolEntry.replayable`, re-asked on the stored args at
   *  replay time: an entry another build kept under an older predicate (it called
   *  navigate+autoRun replayable) must not re-run. Absent = never replayed. */
  replayable?: (args: Record<string, unknown>) => boolean
  /** The tool pins its replay target (`AgentToolEntry.pinReplayTarget`): an entry kept
   *  without a pin (`replayFileId` absent — an older build) is never replayed. */
  pinsTarget?: boolean
}
export type ReplayLookup = (toolName: string) => ReplayTool | undefined
/** A replay deferred by `ready()` is retried this often… */
export const REPLAY_RETRY_MS = 1000
/** Keys this page replayed (or gave up on) — bounded FIFO; a replay runs once per page. */
const replayed = new Set<string>()
/** When each key's replay was first tried on this page — the bound on deferring. */
const replayFirstTried = new Map<string, number>()
function markReplayed(key: string): void {
  replayed.add(key)
  while (replayed.size > CLIENT_TOOL_CALLS_RUN_MAX) {
    const oldest = replayed.values().next().value
    if (oldest === undefined) break
    replayed.delete(oldest)
  }
}

function mayReplay(key: string, page: string): KeptResult | null {
  prune()
  const r = kept.get(key)
  if (!r || !r.replayable || r.keptByPage === page || replayed.has(key) || !r.replayToolName || !r.replayArgs) return null
  return r
}

/** A NEWER kept entry (any — replayable or not, from any page load) acts on the same
 *  target: replaying this one would put the older state back on top of it. Newer =
 *  later `keptAt`; a tie goes to the later-inserted entry (Map order). */
function supersededByNewer(key: string, r: KeptResult): boolean {
  if (!r.target) return false
  let after = false
  for (const [k, o] of kept) {
    if (k === key) { after = true; continue }
    const newer = o.keptAt > r.keptAt || (o.keptAt === r.keptAt && after)
    if (newer && o.target === r.target) return true
  }
  return false
}

/** What a call acts on: the tool and the surface the worker stamped. */
export function clientToolTargetKey(toolName: string, args: Record<string, unknown>): string {
  const t = readClientToolTarget(args)
  return `${toolName}|${t?.workspaceId ?? ''}|${t?.fileId ?? ''}`
}

/**
 * Re-run a kept REPLAYABLE result's handler once on this page load to restore the UI it
 * set — only when another page load kept it (this tab reloaded). Called ONLY from the
 * offer path (the server still offers this wait to this page) and from its own deferred
 * retry below; never from the pump or a page load. Through the tool's
 * readiness gate: not ready → retried every second by a page timer (no later offer is
 * relied on), for at most `DEFERRED_REDISPATCH_MAX_MS`. No claim (UI state on this tab
 * only), and the handler's result is DISCARDED: the kept result stays the answer.
 */
export async function replayKeptResult(key: string, page: string, lookup: ReplayLookup, now: number = Date.now()): Promise<'replayed' | 'deferred' | 'none'> {
  const r = mayReplay(key, page)
  if (!r) return 'none'
  const tool = lookup(r.replayToolName!)
  if (!tool) return 'none'
  // An entry kept without a pin by a tool that pins (an older build's) would bind to
  // whatever editor is active now — possibly another database. Never replayed.
  if (tool.pinsTarget && !r.replayFileId) { markReplayed(key); return 'none' }
  // Re-judge the action now, with today's predicate — the stored flag is a mirror.
  let stillReplayable = false
  try { stillReplayable = !!tool.replayable && tool.replayable(r.replayArgs!) } catch { stillReplayable = false }
  if (!stillReplayable) { markReplayed(key); return 'none' }
  // Per target only the newest replays (keptAt order): an older entry is dropped.
  if (supersededByNewer(key, r)) { markReplayed(key); return 'none' }
  const args = r.replayArgs!
  let ready = true
  try { ready = (tool.ready ? tool.ready(args) : true) && (tool.canResolve ? tool.canResolve(args) : true) } catch { ready = true }
  if (!ready) {
    let first = replayFirstTried.get(key)
    if (first === undefined) {
      first = now
      replayFirstTried.set(key, now)
      while (replayFirstTried.size > CLIENT_TOOL_CALLS_RUN_MAX) {
        const oldest = replayFirstTried.keys().next().value
        if (oldest === undefined) break
        replayFirstTried.delete(oldest)
      }
    }
    if (now - first > DEFERRED_REDISPATCH_MAX_MS) { markReplayed(key); return 'none' }
    scheduleRedispatch('replay', key, REPLAY_RETRY_MS, () => { void replayKeptResult(key, page, lookup).catch(() => {}) })
    return 'deferred'
  }
  // Marked BEFORE the await: a second surface (or offer) in the same tick never re-runs it.
  markReplayed(key)
  try {
    await tool.handler(args, { toolCallId: r.toolCallId, sessionId: r.sessionId })
  } catch (err) {
    console.warn(`[agent] replaying kept client-tool result ${key} after a reload failed (UI only; the kept result still answers):`, err)
  }
  return 'replayed'
}

type ResumeStarter = (sessionId: string, workspaceId: string | undefined) => void
const resumeStarters: ResumeStarter[] = []
const resumeQueue = new Map<string, string | undefined>()

/** Mounted `useAgentSession` hooks register; returns the unregister. A resume
 *  requested while none was mounted runs on the first registration. */
export function registerResumeStarter(fn: ResumeStarter): () => void {
  resumeStarters.push(fn)
  if (resumeQueue.size > 0) {
    const queued = [...resumeQueue]
    resumeQueue.clear()
    for (const [sid, ws] of queued) {
      try { fn(sid, ws) } catch (err) { console.error('[agent] queued resume failed:', err) }
    }
  }
  return () => {
    const i = resumeStarters.indexOf(fn)
    if (i >= 0) resumeStarters.splice(i, 1)
  }
}

/** Start a suspended turn's resume run with the KEPT session and workspace (never a
 *  hook's): a mounted starter (the newest) runs it, else it waits for one. */
export function requestResume(sessionId: string, workspaceId: string | undefined): void {
  const fn = resumeStarters[resumeStarters.length - 1]
  if (fn) fn(sessionId, workspaceId)
  else resumeQueue.set(sessionId, workspaceId)
}

/** The processing poll asks for a resume at most once per this, per session. */
const RESUME_REREQUEST_MS = 10_000
const resumeRequestedAt = new Map<string, number>()
/** Live resume attaches per session — a count: an attach a newer one aborted ends late. */
const resumeAttachLive = new Map<string, number>()

/**
 * R-13: may the processing poll request a resume for `sid` now? `/pending` keeps
 * answering `resumeReady` until the worker claims the rows, and each `beginResume`
 * aborts and restarts the live attach — so not while a resume attach for `sid` is
 * live, nor within 10 s of the last request. True = recorded as requested.
 */
export function takeResumeRequest(sid: string, now: number = Date.now()): boolean {
  if (resumeAttachLive.has(sid)) return false
  const last = resumeRequestedAt.get(sid)
  if (last !== undefined && now - last < RESUME_REREQUEST_MS) return false
  resumeRequestedAt.set(sid, now)
  while (resumeRequestedAt.size > CLIENT_TOOL_CALLS_RUN_MAX) {
    const oldest = resumeRequestedAt.keys().next().value
    if (oldest === undefined) break
    resumeRequestedAt.delete(oldest)
  }
  return true
}

/** A resume attach for `sid` started (`true`) or ended (`false`). */
export function markResumeAttach(sid: string, live: boolean): void {
  const n = (resumeAttachLive.get(sid) ?? 0) + (live ? 1 : -1)
  if (n > 0) resumeAttachLive.set(sid, n)
  else resumeAttachLive.delete(sid)
}

/**
 * The processing poll's decision. `resumeReady` comes FIRST: when the worker carried
 * a result into its suspended row, the page that POSTed it got only `{ok:true}`, and
 * `/pending` then answers `{ pending: null, resumeReady: true }` — falling into the
 * orphan branch would force-idle a turn that only needs its resume run.
 */
export function pollOutcome(
  res: { pending?: { type: string } | null; resumeReady?: boolean } | null | undefined,
  status: string,
): 'resume' | 'run-client-tool' | 'restore-prompt' | 'orphan-check' | 'none' {
  if (res?.resumeReady) return 'resume'
  if (res?.pending) return res.pending.type === 'client-tool-request' ? 'run-client-tool' : 'restore-prompt'
  return status === 'processing' ? 'orphan-check' : 'none'
}

// ─── One tab runs each wait (#352 I1) ───────────────────────────────────────
//
// The page dedupe above is per PAGE; a session is multi-attacher, so every tab that
// shows it gets the worker's request and each ran the handler (an editor action
// applied twice). Before running, a page CLAIMS the wait on the server
// (features/agent/server/client-tool-claim.ts): `run` for 30 s, renewed every 10 s
// while the handler runs, then `ran` for 600 s once the result is KEPT — refreshed
// before every result POST and, for a taken entry, every 5 min until the wait is
// over. A dead claimant blocks the others for at most ~30 s.
//
// Who claims first: a page that can RESOLVE the call's surface claims on its first
// offer; one that can't waits until the wait was first seen on this page ≥ 8 s ago
// (page-wide, so two chat surfaces don't count twice), so a tab with the surface
// wins and, when none has it, one answers the clear error after ~8 s.
//
// Fail OPEN: a claim that errors runs, as before the claim existed — an old API's 404
// at once, a transient failure (network, 5xx) once it has lasted 35 s (R-20); the page
// dedupe and kept results still apply.

export const CLAIM_RENEW_MS = 10_000
/** A page that cannot resolve the surface claims once the wait was first seen this long ago. */
export const UNRESOLVED_CLAIM_AFTER_MS = 8_000

// ── Same-browser hand-off (#352, owner 2026-10-22) ─────────────────────────
// A tab that can't resolve a call waits UNRESOLVED_CLAIM_AFTER_MS before claiming it — but the
// tab that CAN resolve it learns of the wait only by polling (the busy set every 15 s while
// nothing ran, then /pending every 5 s), so it usually learned too late and the tab with no
// editor answered "No database file is open". The waiting tab now tells the browser's other
// tabs at once; one showing that session asks /pending immediately and claims it. Another
// DEVICE still relies on the polls (accepted, owner 2026-10-22).
const HANDOFF_CHANNEL = 'papan-client-tool-handoff'
export interface UnresolvedWaitNotice { sessionId: string; workspaceId?: string }
let handoffChannel: BroadcastChannel | null | undefined
function channel(): BroadcastChannel | null {
  if (handoffChannel !== undefined) return handoffChannel
  try { handoffChannel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(HANDOFF_CHANNEL) } catch { handoffChannel = null }
  return handoffChannel
}
/** Tell this browser's other tabs that this one is waiting on a call it can't resolve. */
export function announceUnresolvedWait(n: UnresolvedWaitNotice): void {
  try { channel()?.postMessage({ type: 'unresolved-wait', sessionId: n.sessionId, workspaceId: n.workspaceId }) } catch { /* best effort: the polls still run */ }
}
/** Another tab of this browser is waiting on a call it can't resolve; returns the unsubscribe. */
export function onUnresolvedWaitAnnounced(fn: (n: UnresolvedWaitNotice) => void): () => void {
  const ch = channel()
  if (!ch) return () => {}
  const listener = (e: MessageEvent) => {
    const d = e.data as { type?: unknown; sessionId?: unknown; workspaceId?: unknown } | null
    if (!d || d.type !== 'unresolved-wait' || typeof d.sessionId !== 'string') return
    fn({ sessionId: d.sessionId, ...(typeof d.workspaceId === 'string' ? { workspaceId: d.workspaceId } : {}) })
  }
  ch.addEventListener('message', listener)
  return () => ch.removeEventListener('message', listener)
}
/** Tests only: forget the channel (a new fake BroadcastChannel per test). */
export function resetHandoffChannelForTests(): void { try { handoffChannel?.close() } catch { /* */ } handoffChannel = undefined }
/** A claim lost to a RUNNING holder is retried after the `run` TTL (30 s) + 5 s. */
export const CLAIM_LOST_RETRY_MS = 35_000
const CLAIM_TIMEOUT_MS = 15_000

export type ClaimOutcome = 'claimed' | 'lost' | 'not-pending' | 'error'
/** WHY a claim errored: `absent` = HTTP 404 (an old API without the route); everything
 *  else (network, timeout, 5xx, 408, 429, 400/401/403 — which can recover after
 *  re-auth) is `transient`. */
export type ClaimErrorReason = 'absent' | 'transient'
export interface ClaimWait { toolCallId: string; waitId?: string }
export interface ClaimCtx { sessionId: string; workspaceId: string | undefined; tenantId: string | null; holder: string }

/**
 * Claim (or renew, or mark `ran`) a wait. Headers are built for `ctx.tenantId` as a
 * kept result's re-POST's are — `null` sends none, never the tenant active now.
 * `workspaceId` is the SESSION's own (`resultWorkspaceId`): the route reads a
 * suspended row there.
 */
export async function claimWait(wait: ClaimWait, ctx: ClaimCtx, phase: 'run' | 'ran' = 'run'): Promise<{ outcome: ClaimOutcome; phase?: 'run' | 'ran'; reason?: ClaimErrorReason }> {
  try {
    const res = await sessionFetch(`${getApiBase()}/api/agent/sessions/${encodeURIComponent(ctx.sessionId)}/client-tool-claim`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...(ctx.tenantId ? { 'x-tenant-id': ctx.tenantId } : {}) },
      ...(typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function' ? { signal: AbortSignal.timeout(CLAIM_TIMEOUT_MS) } : {}),
      body: JSON.stringify({
        toolCallId: wait.toolCallId, holder: ctx.holder, phase,
        ...(wait.waitId ? { waitId: wait.waitId } : {}),
        ...(ctx.workspaceId ? { workspaceId: ctx.workspaceId } : {}),
      }),
    })
    const json = await res.json().catch(() => null) as { claimed?: unknown; phase?: unknown; code?: unknown } | null
    if (res.status === 409 && json?.code === 'CLIENT_TOOL_NOT_PENDING') return { outcome: 'not-pending' }
    if (res.status === 404) return { outcome: 'error', reason: 'absent' }
    if (!res.ok || !json) return { outcome: 'error', reason: 'transient' }
    if (json.claimed === true) return { outcome: 'claimed' }
    if (json.claimed === false) return { outcome: 'lost', phase: json.phase === 'ran' ? 'ran' : 'run' }
    return { outcome: 'error', reason: 'transient' }
  } catch {
    return { outcome: 'error', reason: 'transient' }
  }
}

const renewals = new Set<() => void>()

/**
 * Renew a `run` claim every 10 s while the handler runs. `lost` / `not-pending`
 * (another tab took it after a network gap, or the wait ended) → stop and call
 * `onLost`; an `error` keeps renewing; the session that took it ending stops it quietly (#1316). Returns stop.
 */
export function renewClaim(wait: ClaimWait, ctx: ClaimCtx, onLost: () => void): () => void {
  let stopped = false
  let inFlight = false
  // #1316: renewed only within the session that took the claim — a renewal after it ended would claim the wait as the
  // NEXT session (claimWait folds a dropped answer into `error`, so the session is asked here, before and after)
  const start = currentSessionSignal()
  const timer = setInterval(() => {
    if (stopped || inFlight) return
    if (sessionEndedSince(start)) { stop(); return }
    inFlight = true
    void claimWait(wait, ctx, 'run').then((r) => {
      inFlight = false
      if (stopped) return
      if (sessionEndedSince(start)) { stop(); return }
      if (r.outcome === 'lost' || r.outcome === 'not-pending') { stop(); onLost() }
    })
  }, CLAIM_RENEW_MS)
  const stop = (): void => {
    stopped = true
    clearInterval(timer)
    renewals.delete(stop)
  }
  renewals.add(stop)
  return stop
}

/** When each wait was FIRST seen on this page (ms epoch), by `waitKey` — bounded FIFO. */
const firstSeenAt = new Map<string, number>()

/** The first time this page saw the wait (recorded now when new). */
export function firstSeen(key: string, now: number = Date.now()): number {
  const at = firstSeenAt.get(key)
  if (at !== undefined) return at
  firstSeenAt.set(key, now)
  while (firstSeenAt.size > CLIENT_TOOL_CALLS_RUN_MAX) {
    const oldest = firstSeenAt.keys().next().value
    if (oldest === undefined) break
    firstSeenAt.delete(oldest)
  }
  return now
}

/** Page-wide re-dispatch timers, ONE per (kind, wait) — a second surface schedules none. Each keeps the session it was
 *  scheduled in (#1316 review round 2, r2 B2-1): a timer whose session ended does nothing when it fires, so it never
 *  holds its slot — the next session's schedule replaces it (`liveRedispatch`), else a sibling wait whose holder tab
 *  died would never be claimed here. */
const redispatchTimers = new Map<string, { timer: ReturnType<typeof setTimeout>; session: AbortSignal | undefined }>()
/** Is a re-dispatch of this id scheduled IN A SESSION THAT LIVES? One left by an ended session is dropped here. */
function liveRedispatch(id: string): boolean {
  const entry = redispatchTimers.get(id)
  if (!entry) return false
  if (!sessionEndedSince(entry.session)) return true
  clearTimeout(entry.timer)
  redispatchTimers.delete(id)
  if (id.startsWith('defer:')) deferredSince.delete(id.slice('defer:'.length))   // its deferral window was that session's
  return false
}
function scheduleRedispatch(kind: 'seen' | 'retry' | 'defer' | 'claim-error' | 'replay', key: string, delayMs: number, fn: () => void): void {
  const id = `${kind}:${key}`
  if (liveRedispatch(id)) return
  const session = currentSessionSignal()
  redispatchTimers.set(id, {
    session,
    timer: setTimeout(() => {
      redispatchTimers.delete(id)
      // #1316 review round 2 (M2): a timer whose session ended does nothing — every kind (a `replay` re-runs a
      // replayable handler, which would act in the NEXT session's editors: they are not remounted)
      if (sessionEndedSince(session)) {
        if (kind === 'defer') deferredSince.delete(key)   // its deferral window was that session's
        return
      }
      try { fn() } catch (err) { console.error('[agent] client-tool re-dispatch failed:', err) }
    }, Math.max(0, delayMs)),
  })
}

/** A timer re-dispatch deferred by the readiness gate is re-armed this often. */
export const DEFERRED_REDISPATCH_MS = 5000
/** …for at most this long from its first deferral — past the worker's 5-minute client-tool
 *  wait (a resumed wait is offered again by `/pending`). A tool's own `maxDeferMs` ends the
 *  deferral sooner (`shouldDeferClientTool` stops deferring and the dispatch claims). */
export const DEFERRED_REDISPATCH_MAX_MS = 6 * 60 * 1000
const deferredSince = new Map<string, number>()

/**
 * Re-arm a page TIMER's re-dispatch (the 35 s claim-lost retry, the ~8 s can't-resolve
 * timer) that the readiness gate deferred — an editor remounting, a preview reloading.
 * Those timers exist because no later offer can be relied on (the live offer is
 * one-shot; parallel siblings may never show on `/pending`), so a deferral that simply
 * returned could leave a dead claimant never replaced. ONE timer per wait; `false` once
 * the wait has been deferring for `DEFERRED_REDISPATCH_MAX_MS` (nothing is scheduled).
 */
export function rearmDeferredRedispatch(key: string, fn: () => void, now: number = Date.now()): boolean {
  liveRedispatch(`defer:${key}`)   // an ended session's re-arm (and its deferral window) is dropped first
  let since = deferredSince.get(key)
  if (since === undefined) {
    since = now
    deferredSince.set(key, now)
    while (deferredSince.size > CLIENT_TOOL_CALLS_RUN_MAX) {
      const oldest = deferredSince.keys().next().value
      if (oldest === undefined) break
      deferredSince.delete(oldest)
    }
  }
  if (now - since > DEFERRED_REDISPATCH_MAX_MS) return false
  scheduleRedispatch('defer', key, DEFERRED_REDISPATCH_MS, fn)
  return true
}

/** Retry delays after a TRANSIENT claim failure, per scheduled retry (the last repeats). */
export const CLAIM_ERROR_RETRY_MS = [5_000, 10_000, 20_000] as const
/** A transient claim outage fails open only once this long has passed since the wait's
 *  FIRST transient failure (R-20) — by elapsed time, never by attempts: fresh `/pending`
 *  offers retry the claim too, and counting them failed open after ~15 s. */
export const CLAIM_ERROR_FAIL_OPEN_MS = 35_000
/** Per `waitKey`: when its current outage began, and how many retries were scheduled —
 *  bounded FIFO. Cleared by any answer that is not a transient error. */
const claimErrors = new Map<string, { since: number; scheduled: number }>()
function claimErrorState(key: string, now: number): { since: number; scheduled: number } {
  let st = claimErrors.get(key)
  if (!st) {
    st = { since: now, scheduled: 0 }
    claimErrors.set(key, st)
    while (claimErrors.size > CLIENT_TOOL_CALLS_RUN_MAX) {
      const oldest = claimErrors.keys().next().value
      if (oldest === undefined) break
      claimErrors.delete(oldest)
    }
  }
  return st
}

/** Waits this page is claiming right now — taken synchronously before the await, so
 *  two surfaces delivering one offer together send one claim. */
const claiming = new Set<string>()

export interface ClaimedRun {
  offer: ClientToolOffer
  /** The session (captured by the dispatcher — never re-read after an await). */
  sessionId: string
  /** The session's own workspace: `resultWorkspaceId(offer, hookWorkspaceId)`. */
  workspaceId: string | undefined
  /** The tenant of THIS dispatch: the claim and every re-send of the result go under it. */
  tenantId: string | null
  /** This page (`CLIENT_ID`). */
  holder: string
  /** `entry.canResolve?.(args) ?? true` — can this page act on the call's surface now? */
  canResolve: boolean
  /** `entry.replayable?.(args) ?? false` on the dispatched args — a reloaded page may
   *  re-run the kept result once to restore its UI (owner ruling 2026-10-19). */
  replayable?: boolean
  /** R-22: the dispatched args pinned to the exact surface the handler is about to
   *  drive (`AgentToolEntry.pinReplayTarget`) — asked right before the handler. */
  pinReplayArgs?: () => Record<string, unknown> | undefined
  /** The dispatched args — kept for a replay (only when `replayable`). */
  args?: Record<string, unknown>
  /** The tool's handler with its args bound. */
  handler: () => Promise<unknown>
  /** Sends a kept result (`postKeptResult`). */
  post: (key: string) => Promise<void>
  /** Re-dispatch the SAME offer with the same captured session and tenant — for the
   *  page's own timers: a later offer is not relied on (the live offer is one-shot,
   *  and `/pending` may never show this wait again: parallel siblings). */
  redispatch: () => void
}

/**
 * The claim-gated tail of a client-tool dispatch, after the kept-result decision,
 * the readiness gate and the session check. `'ran'` = this page ran the handler,
 * kept its result and sent it.
 */
export async function runClaimedOffer(r0: ClaimedRun): Promise<'ran' | 'waiting' | 'lost' | 'not-pending' | 'busy'> {
  // #1316 review round 1: the claim belongs to the session it started in. Every timer this offer schedules re-dispatches
  // only while that session lives (a re-dispatch after it would claim — and at fail-open RUN — the wait as the NEXT
  // session); the `/pending` poll of the session then here re-offers a wait that is still open.
  const start = currentSessionSignal()
  const r: ClaimedRun = { ...r0, redispatch: () => { if (!sessionEndedSince(start)) r0.redispatch() } }
  const key = waitKey(r.offer)
  const now = Date.now()
  if (!r.canResolve) {
    const age = now - firstSeen(key, now)
    // First sight only: tell the browser's other tabs, so one that holds the surface claims first.
    if (age === 0) announceUnresolvedWait({ sessionId: r.sessionId, workspaceId: r.workspaceId })
    if (age < UNRESOLVED_CLAIM_AFTER_MS) {
      scheduleRedispatch('seen', key, UNRESOLVED_CLAIM_AFTER_MS - age, r.redispatch)
      return 'waiting'
    }
  }
  if (claiming.has(key)) return 'busy'
  claiming.add(key)
  const wait: ClaimWait = { toolCallId: r.offer.toolCallId, ...(r.offer.waitId ? { waitId: r.offer.waitId } : {}) }
  const ctx: ClaimCtx = { sessionId: r.sessionId, workspaceId: r.workspaceId, tenantId: r.tenantId, holder: r.holder }
  try {
    const c = await claimWait(wait, ctx, 'run')
    // the session ended while the claim was out (claimWait folds a dropped answer into `error`): nothing runs, nothing
    // is scheduled
    if (sessionEndedSince(start)) return 'waiting'
    if (c.outcome !== 'error' || c.reason === 'absent') claimErrors.delete(key)
    // The wait is over (or another wait's): nothing runs, and it is NOT marked run.
    if (c.outcome === 'not-pending') return 'not-pending'
    if (c.outcome === 'lost') {
      // A RUNNING holder may be dead, and offers can't be relied on to bring the wait
      // back: retry after its claim could have expired. Each retry that loses to a
      // `run` holder schedules the next, so this repeats every 35 s until the claim is
      // won, turns `ran` (the holder has the result) or the wait is `not-pending`.
      if (c.phase !== 'ran') scheduleRedispatch('retry', key, CLAIM_LOST_RETRY_MS, r.redispatch)
      return 'lost'
    }
    if (c.outcome === 'error' && c.reason !== 'absent') {
      // A TRANSIENT failure (network, 5xx, 429…): running now could double-run the
      // handler, because another tab may be getting the same blip. Inside 35 s of the
      // wait's FIRST transient failure every offer or timer only retries the claim (R-20),
      // and a re-dispatch is kept scheduled (5 s, 10 s, 20 s) so a page with no offers
      // still retries; past it, fail open, so a sustained outage strands no wait.
      // (404 = old API: fail open at once.)
      const failedAt = Date.now()
      const st = claimErrorState(key, failedAt)
      const elapsed = failedAt - st.since
      if (elapsed < CLAIM_ERROR_FAIL_OPEN_MS) {
        if (!liveRedispatch(`claim-error:${key}`)) {
          const delay = CLAIM_ERROR_RETRY_MS[Math.min(st.scheduled, CLAIM_ERROR_RETRY_MS.length - 1)]
          st.scheduled++
          scheduleRedispatch('claim-error', key, Math.min(delay, CLAIM_ERROR_FAIL_OPEN_MS - elapsed), r.redispatch)
        }
        return 'waiting'
      }
      claimErrors.delete(key)
    }
    // 'claimed', or 'error' (absent, or out of retries → fail open). Once per WAIT on this page (#352 R9).
    if (!takeClientToolRun(r.offer)) return 'busy'
  } finally {
    claiming.delete(key)
  }
  const stopRenew = renewClaim(wait, ctx, () => {
    // The server takes only the first answer, so the result is still kept and sent.
    console.warn(`[agent] client-tool claim for ${key} was lost while its handler ran — finishing; the server takes the first answer (#352 I1)`)
  })
  // R-22: pin what the handler is about to drive (it picks the same way, at its start).
  let pinned: Record<string, unknown> | undefined
  try { pinned = r.pinReplayArgs?.() } catch { pinned = undefined }
  let result: unknown
  try {
    result = await r.handler()
  } catch (err) {
    result = { success: false, error: err instanceof Error ? err.message : 'client tool failed' }
  } finally {
    stopRenew()
  }
  // KEEP first (synchronous, mirrored to sessionStorage); each POST attempt then
  // fires the claim's `ran` refresh alongside the send (not awaited — concurrent, which
  // is fine: ≥~20 s of the 30 s `run` claim remains, since renewal ran to here). Only
  // once the result is safe in this tab, so a crash before it leaves just the `run` claim.
  keepResult(key, {
    tenantId: r.tenantId, toolCallId: r.offer.toolCallId, holder: r.holder, sessionId: r.sessionId,
    workspaceId: r.workspaceId, ...(r.offer.waitId ? { waitId: r.offer.waitId } : {}),
    body: JSON.stringify(result), keptByPage: r.holder, target: clientToolTargetKey(r.offer.toolName, pinned ?? r.args ?? r.offer.args),
    ...(r.replayable ? { replay: { toolName: r.offer.toolName, args: pinned ?? r.args ?? r.offer.args } } : {}),
    ...(r.replayable && pinned ? { replayFileId: readClientToolTarget(pinned)?.fileId } : {}),
  })
  await r.post(key)
  return 'ran'
}

/**
 * Refresh a kept result's `ran` claim with everything the entry carries — its holder
 * (a reloaded tab has a new CLIENT_ID, and still refreshes the claim it took), tenant,
 * session, workspace and wait. Called before EVERY result POST and by the pump for
 * taken entries; `not-pending` ends a taken entry's refreshes.
 */
export async function refreshKeptClaim(key: string): Promise<void> {
  const k = kept.get(key)
  if (!k) return
  const r = await claimWait(
    { toolCallId: k.toolCallId, ...(k.waitId ? { waitId: k.waitId } : {}) },
    { sessionId: k.sessionId, workspaceId: k.workspaceId, tenantId: k.tenantId, holder: k.holder },
    'ran',
  )
  noteClaim(key, r.outcome)
}

/** Test-only: clears the in-memory store, the pump's started flag and interval, the
 *  resume queue + starters, the run / defer bookkeeping and the claim timers. */
export function __resetClientToolRunForTests(): void {
  kept.clear()
  if (pumpTimer !== null) clearInterval(pumpTimer)
  pumpTimer = null
  refreshing.clear()
  resumeStarters.length = 0
  resumeQueue.clear()
  resumeRequestedAt.clear()
  resumeAttachLive.clear()
  resetClientToolRunsForTest()
  resetClientToolDeferTimesForTest()
  for (const stop of [...renewals]) stop()
  for (const t of redispatchTimers.values()) clearTimeout(t.timer)
  redispatchTimers.clear()
  claiming.clear()
  claimErrors.clear()
  firstSeenAt.clear()
  deferredSince.clear()
  replayed.clear()
  replayFirstTried.clear()
}
